import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button, PageHeader } from '@/shared/components';
import {
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components/SurfaceStates';
import { useApi, type CleaningTeamResponse } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { CleaningTeamEditorDialog } from './CleaningTeamEditorDialog';
import { CleaningTeamsList } from './CleaningTeamsList';
import { cleaningTeamKeys } from './teamKeys';
import { useTeamSave } from './useTeamSave';

const CARD = 'border-hairline rounded-tile overflow-hidden border bg-white';

/** Reinigungsteams im Basis-Stil: Leitungen, Mitglieder und Objektfreigaben je Team. */
export function CleaningTeamsPage() {
  const api = useApi();
  const [editing, setEditing] = useState<CleaningTeamResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const teams = useQuery({
    queryKey: cleaningTeamKeys.list(),
    queryFn: () => api.cleaningTeams.list(),
  });
  const problem = useApiProblem(teams.error);
  const close = () => {
    setCreating(false);
    setEditing(null);
  };
  const { submit, saving, online } = useTeamSave(editing, close);
  const count = teams.data?.length;
  return (
    <>
      <PageHeader
        title="Teams"
        description={
          count === undefined
            ? 'Leitungen, Mitglieder und Objektfreigaben der Reinigung.'
            : `${count} ${count === 1 ? 'Team' : 'Teams'} · Leitungen, Mitglieder und Objektfreigaben.`
        }
        actions={
          <Button
            variant="brand"
            leftIcon={<Plus size={14} aria-hidden />}
            onClick={() => setCreating(true)}
          >
            Neues Team
          </Button>
        }
      />
      {teams.isError && teams.data !== undefined && (
        <div className={`${CARD} mb-3`}>
          <SurfaceStaleHint onRetry={() => void teams.refetch()} retrying={teams.isFetching} />
        </div>
      )}
      {teams.data === undefined ? (
        <div className={CARD}>
          {teams.isError ? (
            <SurfaceLoadError
              message={problemText(problem, 'Die Teams konnten nicht geladen werden.')}
              onRetry={() => void teams.refetch()}
              retrying={teams.isFetching}
            />
          ) : online ? (
            <SurfaceLoading label="Teams werden geladen" rows={3} />
          ) : (
            <p role="status" className="text-ink-soft p-5 text-sm">
              Keine Verbindung – die Teams laden, sobald du wieder Netz hast.
            </p>
          )}
        </div>
      ) : teams.data.length === 0 ? (
        <section className={`${CARD} flex flex-col gap-2 p-5`}>
          <b className="text-[15px]">Noch keine Teams</b>
          <p className="text-ink-soft text-sm">
            Leg ein Team an, um Leitung, Mitglieder und Objekte zusammenzuführen.
          </p>
          <Button
            variant="secondary"
            size="lg"
            className="self-start"
            onClick={() => setCreating(true)}
          >
            Neues Team
          </Button>
        </section>
      ) : (
        <CleaningTeamsList teams={teams.data} onEdit={setEditing} />
      )}
      <CleaningTeamEditorDialog
        team={editing}
        open={creating || editing !== null}
        loading={saving}
        onOpenChange={(next) => !next && close()}
        onSubmit={submit}
      />
    </>
  );
}
